import React, { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaGripVertical, FaArrowsAlt } from 'react-icons/fa';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  orderBy,
  addDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase/firestore';
import Spinner from '../../Components/Spinner';
import { useProductForm } from '../../hooks/useProductForm';
import ProductFormModal from '../../pages/admin/ProductFormModal';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const AdminProductsPanel = () => {
  // States
  const [products, setProducts] = useState([]);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoryEditId, setCategoryEditId] = useState(null);
  const [categoryInput, setCategoryInput] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [isReorderingCategories, setIsReorderingCategories] = useState(false);
  const [originalCategoryOrder, setOriginalCategoryOrder] = useState([]);
  const [isSavingCategoryOrder, setIsSavingCategoryOrder] = useState(false);

  const {
    form,
    formError,
    formLoading,
    resetForm,
    fillForm,
    handleFormChange,
    handleCategoryChange,
    handleSpecificationChange,
    addSpecification,
    removeSpecification,
    handleFormSubmit,
    handleVariationChange,
    addVariation,
    removeVariation,
  } = useProductForm(() => {
    setShowModal(false);
    fetchProducts();
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'products'), 
        orderBy('displayOrder', 'asc')
      );
      const snapshot = await getDocs(q);
      const productsData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        displayOrder: doc.data().displayOrder || 0
      }));
      setProducts(productsData);
      setOriginalOrder(productsData.map(p => p.id));
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const q = query(collection(db, 'categories'), orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const cats = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        order: doc.data().order || 0,
      }));
      setCategories(cats);
      setOriginalCategoryOrder(cats.map(c => c.id));
    } catch (err) {
      console.error('Error loading categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Обработка перетаскивания категорий - исправлено: начинаем с 1
  const handleCategoryDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(categories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Обновляем локальное состояние с новым порядком, начиная с 1
    const updatedCategories = items.map((item, index) => ({
      ...item,
      order: index + 1 // Начинаем с 1 вместо 0
    }));
    
    setCategories(updatedCategories);

    try {
      // Сохраняем новый порядок в Firestore
      const batch = writeBatch(db);
      updatedCategories.forEach((cat, index) => {
        const ref = doc(db, 'categories', cat.id);
        batch.update(ref, { order: index + 1 }); // Начинаем с 1 вместо 0
      });
      await batch.commit();
      
      console.log('Порядок категорий сохранен!');
      
      // Если в режиме реорганизации, обновляем исходный порядок
      if (isReorderingCategories) {
        setOriginalCategoryOrder(updatedCategories.map(c => c.id));
      }
    } catch (err) {
      console.error('Error saving category order:', err);
      
      // Восстанавливаем предыдущий порядок при ошибке
      if (isReorderingCategories) {
        fetchCategories();
      }
      alert('Ошибка при сохранении порядка категорий');
    }
  };

  // Включение/выключение режима изменения порядка категорий
  const toggleCategoryReordering = () => {
    if (isReorderingCategories) {
      // Выходим из режима
      fetchCategories(); // Обновляем данные из базы
      setIsReorderingCategories(false);
    } else {
      // Входим в режим реорганизации
      setOriginalCategoryOrder(categories.map(c => c.id));
      setIsReorderingCategories(true);
    }
  };

  // Сохранение нового порядка категорий
  const saveNewCategoryOrder = async () => {
    setIsReorderingCategories(false);
    alert('Порядок категорий сохранен!');
  };

  // Отмена изменения порядка категорий
  const cancelCategoryReordering = () => {
    fetchCategories();
    setIsReorderingCategories(false);
  };

  // Обработка перетаскивания товаров - исправлено: начинаем с 1
  const handleProductDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(products);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Обновляем локальное состояние с новым порядком, начиная с 1
    const updatedProducts = items.map((item, index) => ({
      ...item,
      displayOrder: index + 1 // Начинаем с 1 вместо 0
    }));
    
    setProducts(updatedProducts);

    try {
      // Сохраняем новый порядок в Firestore
      const batch = writeBatch(db);
      updatedProducts.forEach((product, index) => {
        const ref = doc(db, 'products', product.id);
        batch.update(ref, { displayOrder: index + 1 }); // Начинаем с 1 вместо 0
      });
      await batch.commit();
      
      console.log('Порядок товаров сохранен!');
      
      // Если в режиме реорганизации, обновляем исходный порядок
      if (isReordering) {
        setOriginalOrder(updatedProducts.map(p => p.id));
      }
    } catch (err) {
      console.error('Error saving product order:', err);
      
      // Восстанавливаем предыдущий порядок при ошибке
      if (isReordering) {
        fetchProducts();
      }
      alert('Ошибка при сохранении порядка товаров');
    }
  };

  // Включение/выключение режима изменения порядка товаров
  const toggleReordering = () => {
    if (isReordering) {
      fetchProducts();
      setIsReordering(false);
    } else {
      setOriginalOrder(products.map(p => p.id));
      setIsReordering(true);
    }
  };

  const handleAddCategory = async () => {
    const name = categoryInput.trim();
    if (!name) return setCategoryError('Название не может быть пустым');
    if (categories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      return setCategoryError('Такая категория уже существует');
    }
    setCategoryError('');
    try {
      // Добавляем новую категорию в конец, начиная порядок с 1
      const newOrder = categories.length > 0 
        ? Math.max(...categories.map(c => c.order)) + 1 
        : 1;
      
      await addDoc(collection(db, 'categories'), {
        name,
        order: newOrder, // Используем следующий порядковый номер
      });
      setCategoryInput('');
      setCategoryEditId(null);
      fetchCategories();
    } catch (err) {
      console.error('Error adding category:', err);
      setCategoryError('Ошибка при добавлении категории');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Удалить эту категорию?')) return;
    try {
      await deleteDoc(doc(db, 'categories', id));
      fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Ошибка при удалении категории');
    }
  };

  const handleSaveCategory = async () => {
    const name = categoryInput.trim();
    if (!name) return setCategoryError('Название не может быть пустым');
    if (
      categories.some(
        c => c.name.toLowerCase() === name.toLowerCase() && c.id !== categoryEditId
      )
    ) {
      return setCategoryError('Такая категория уже существует');
    }
    setCategoryError('');
    try {
      await updateDoc(doc(db, 'categories', categoryEditId), { name });
      setCategoryInput('');
      setCategoryEditId(null);
      fetchCategories();
    } catch (err) {
      console.error('Error updating category:', err);
      setCategoryError('Ошибка при обновлении категории');
    }
  };

  const handleCancelEdit = () => {
    setCategoryEditId(null);
    setCategoryInput('');
    setCategoryError('');
  };

  const handleCategoryClick = (cat) => {
    if (!isReorderingCategories) {
      setCategoryEditId(cat.id);
      setCategoryInput(cat.name);
    }
  };

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить этот товар?')) return;
    try {
      await deleteDoc(doc(db, 'products', id));
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  };

  const handleDuplicate = async (product) => {
    const { id, ...copy } = product;
    try {
      // Находим максимальный displayOrder и добавляем 1
      const maxDisplayOrder = products.length > 0 
        ? Math.max(...products.map(p => p.displayOrder)) 
        : 0;
      
      await addDoc(collection(db, 'products'), {
        ...copy,
        title: `${copy.title} (копия)`,
        displayOrder: maxDisplayOrder + 1, // Добавляем в конец с правильным порядком
      });
      fetchProducts();
    } catch (err) {
      console.error('Error duplicating product:', err);
    }
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (product) => {
    fillForm(product);
    setShowModal(true);
  };

  const filteredProducts = products.filter(p =>
    p.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categoryOptions = categories.map(c => ({ value: c.name, label: c.name }));

  return (
    <div className="w-full min-h-screen p-6 text-black max-w-5xl mx-auto">

      {/* Categories */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Категории</h2>
          <div className="flex gap-2">
            {!isReorderingCategories ? (
              <button
                onClick={toggleCategoryReordering}
                className="bg-purple-600 text-white hover:bg-purple-700 px-4 py-2 rounded font-semibold flex items-center gap-2"
                title="Изменить порядок категорий"
              >
                <FaArrowsAlt /> Изменить порядок
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={saveNewCategoryOrder}
                  className="bg-green-600 text-white hover:bg-green-700 px-4 py-2 rounded font-semibold flex items-center gap-2"
                  disabled={isSavingCategoryOrder}
                >
                  {isSavingCategoryOrder ? 'Сохранение...' : 'Завершить редактирование'}
                </button>
                <button
                  onClick={cancelCategoryReordering}
                  className="bg-gray-600 text-white hover:bg-gray-700 px-4 py-2 rounded font-semibold"
                >
                  Отмена
                </button>
              </div>
            )}
          </div>
        </div>
        
        {loadingCategories ? (
          <Spinner />
        ) : (
          <div className="bg-gray-100 p-4 rounded shadow max-w-full">
            <DragDropContext onDragEnd={handleCategoryDragEnd}>
              <Droppable droppableId="category-list" direction="horizontal">
                {(provided) => (
                  <div
                    className="flex gap-2 flex-wrap"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {categories.map((cat, index) => (
                      <Draggable draggableId={cat.id} index={index} key={cat.id}>
                        {(drag) => (
                          <div
                            ref={drag.innerRef}
                            {...drag.draggableProps}
                            {...drag.dragHandleProps}
                            className={`flex items-center rounded-full px-4 py-1 text-sm font-medium relative cursor-pointer select-none ${
                              isReorderingCategories 
                                ? 'bg-purple-200 cursor-move border-2 border-purple-400' 
                                : 'bg-yellow-200 cursor-pointer'
                            }`}
                            onClick={() => handleCategoryClick(cat)}
                          >
                            {isReorderingCategories && (
                              <FaGripVertical className="mr-2 text-purple-600" />
                            )}
                            <span className={`${isReorderingCategories ? 'pr-8' : 'pr-6'}`}>
                              {cat.name} {isReorderingCategories && `(${index + 1})`}
                            </span>
                            {!isReorderingCategories && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCategory(cat.id);
                                }}
                                className="text-red-600 hover:text-red-800 p-1 rounded-full absolute right-1 top-1/2 -translate-y-1/2"
                                title="Удалить категорию"
                              >
                                <FaTimes size={12} />
                              </button>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    
                    {/* Кнопка добавления категории - скрываем в режиме реорганизации */}
                    {categoryEditId === null && !isReorderingCategories && (
                      <button
                        onClick={() => {
                          setCategoryEditId('new');
                          setCategoryInput('');
                        }}
                        className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white hover:bg-blue-700 text-xl font-bold"
                        title="Добавить категорию"
                      >
                        <FaPlus />
                      </button>
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {/* Форма добавления/редактирования категории */}
            {categoryEditId && !isReorderingCategories && (
              <div className="mt-4 flex items-center gap-2 max-w-md">
                <input
                  type="text"
                  placeholder={categoryEditId === 'new' ? "Новая категория" : "Редактировать категорию"}
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  className="border border-gray-400 rounded px-3 py-2 flex-grow"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      categoryEditId === 'new' ? handleAddCategory() : handleSaveCategory();
                    }
                  }}
                />
                <button
                  onClick={categoryEditId === 'new' ? handleAddCategory : handleSaveCategory}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  disabled={!categoryInput.trim()}
                >
                  {categoryEditId === 'new' ? 'Добавить' : 'Сохранить'}
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
                >
                  Отмена
                </button>
              </div>
            )}

            {categoryError && (
              <div className="mt-2 p-2 bg-red-100 text-red-700 rounded text-sm">
                {categoryError}
              </div>
            )}
            
            {/* Информационное сообщение в режиме реорганизации */}
            {isReorderingCategories && (
              <div className="mt-4 p-3 bg-blue-100 text-blue-800 rounded text-sm">
                <p className="font-semibold">Режим изменения порядка категорий</p>
                <p>Перетащите категории для изменения их порядка. Изменения сохраняются автоматически при перетаскивании.</p>
                <p className="mt-1 text-xs">Нажмите "Завершить редактирование" чтобы выйти из режима или "Отмена" чтобы отменить изменения.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Products header and controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold">Товары</h1>
          {!isReordering ? (
            <button
              onClick={toggleReordering}
              className="bg-purple-600 text-white hover:bg-purple-700 px-4 py-2 rounded font-semibold flex items-center gap-2"
            >
              <FaGripVertical /> Изменить порядок
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setIsReordering(false);
                  alert('Порядок товаров сохранен!');
                }}
                className="bg-green-600 text-white hover:bg-green-700 px-4 py-2 rounded font-semibold"
              >
                Завершить редактирование
              </button>
              <button
                onClick={() => {
                  fetchProducts();
                  setIsReordering(false);
                }}
                className="bg-gray-600 text-white hover:bg-gray-700 px-4 py-2 rounded font-semibold"
              >
                Отмена
              </button>
            </div>
          )}
        </div>
        <button
          onClick={openAddModal}
          className="bg-green-600 text-white hover:bg-blue-700 px-4 py-2 rounded font-semibold"
        >
          + Добавить товар
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Поиск по имени..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full p-2 rounded border border-gray-300 mb-4"
      />

      {/* Products list */}
      {loading ? (
        <Spinner />
      ) : filteredProducts.length === 0 ? (
        <p className="text-gray-600">Товары не найдены</p>
      ) : (
        <div className="max-h-[70vh] bg-gray-100 overflow-y-auto pr-2 mb-10">
          {isReordering ? (
            // Режим изменения порядка с Drag & Drop
            <DragDropContext onDragEnd={handleProductDragEnd}>
              <Droppable droppableId="product-list">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="divide-y divide-gray-200"
                  >
                    {filteredProducts.map((product, index) => (
                      <Draggable 
                        draggableId={product.id} 
                        index={index} 
                        key={product.id}
                      >
                        {(drag) => (
                          <div
                            ref={drag.innerRef}
                            {...drag.draggableProps}
                            {...drag.dragHandleProps}
                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-300 cursor-move bg-gray-200 border-l-4 border-purple-500"
                          >
                            <div className="flex items-center gap-4">
                              <FaGripVertical className="text-gray-600" />
                              <div className="w-10 h-10 flex items-center justify-center bg-purple-100 rounded">
                                <span className="font-bold text-purple-700">{index + 1}</span>
                              </div>
                              <img
                                src={product.image}
                                alt={product.title}
                                className="w-12 h-12 object-cover rounded"
                              />
                              <div>
                                <p className="font-semibold">{product.title}</p>
                                <p className="text-gray-600 text-sm">
                                  {product.price}€ • Остаток: {product.stock}
                                </p>
                              </div>
                            </div>
                            <div className="text-gray-500 text-sm">
                              Перетащите для изменения порядка
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            // Обычный режим просмотра
            <div className="divide-y divide-gray-200">
              {filteredProducts.map((product) => {
                const isExpanded = expandedId === product.id;
                return (
                  <div key={product.id} className="bg-white hover:bg-gray-50">
                    <div
                      onClick={() => toggleExpand(product.id)}
                      className="flex items-center justify-between px-4 py-3 cursor-pointer"
                    >
                      <div className="flex items-center gap-4">
                        <img
                          src={product.image}
                          alt={product.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                        <div>
                          <p className="font-semibold">{product.title}</p>
                          <p className="text-gray-600 text-sm">
                            {product.price}€ • Остаток: {product.stock}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(product);
                          }}
                          className="bg-yellow-400 hover:bg-yellow-500 px-3 py-1 text-sm rounded"
                        >
                          Редактировать
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicate(product);
                          }}
                          className="bg-blue-400 hover:bg-blue-500 px-3 py-1 text-sm rounded"
                        >
                          Дублировать
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(product.id);
                          }}
                          className="bg-red-400 hover:bg-red-500 px-3 py-1 text-sm rounded"
                        >
                          Удалить
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="bg-gray-50 px-8 py-3 text-sm text-gray-700 space-y-2 border-t">
                        <p><span className="font-semibold">Порядковый номер:</span> {product.displayOrder}</p>
                        <p><span className="font-semibold">Категории:</span> {product.categories && Array.isArray(product.categories) ? product.categories.map(c => c.name).join(', ') : 'Нет категорий'}</p>
                        <p><span className="font-semibold">Краткое описание:</span> {product.shortDescription || 'Нет описания'}</p>
                        <p><span className="font-semibold">Полное описание:</span> {product.fullDescription || 'Нет описания'}</p>
                        <div>
                          <span className="font-semibold">Характеристики:</span>
                          <ul className="list-disc ml-6 mt-1 space-y-1">
                            {Array.isArray(product.specifications) && product.specifications.length > 0 ? (
                              product.specifications.map((spec, idx) => (
                                <li key={idx} className="text-gray-600">
                                  <span className="font-medium">{spec.key}</span>: {spec.value}
                                </li>
                              ))
                            ) : (
                              <li className="text-gray-400">Нет характеристик</li>
                            )}
                          </ul>
                        </div>
                        <p><span className="font-semibold">Рейтинг:</span> {product.rating || 0}</p>
                        <p><span className="font-semibold">Отзывы:</span> {product.reviews || 0}</p>
                        {product.discount && product.discount > 0 && (
                          <p><span className="font-semibold">Скидка:</span> {product.discount}%</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {showModal && (
        <ProductFormModal
          form={form}
          formError={formError}
          formLoading={formLoading}
          onClose={() => setShowModal(false)}
          onSubmit={handleFormSubmit}
          onChange={handleFormChange}
          onCategoryChange={handleCategoryChange}
          categoryOptions={categoryOptions}
          onSpecChange={handleSpecificationChange}
          onAddSpec={addSpecification}
          onRemoveSpec={removeSpecification}
          onVariationChange={handleVariationChange}
          onAddVariation={addVariation}
          onRemoveVariation={removeVariation}
        />
      )}
    </div>
  );
};

export default AdminProductsPanel;